// src/store/slices/redemptionSlice.js
// ENHANCED VERSION - Adds missing thunks for complete workflow

import { createSlice } from "@reduxjs/toolkit";
import {
  createRedemptionThunk,
  getAllRedemptionsThunk,
  getRedemptionByIdThunk,
  getCustomerRedemptionsThunk,
  approveRedemptionThunk,
  cancelRedemptionThunk,
  // NEW THUNKS NEEDED:
  rejectRedemptionThunk,
  updatePaymentThunk,
  completeRedemptionThunk,
  getPendingApprovalsThunk,
  getRedemptionStatsThunk,
} from "../../thunks/jewelleryPlan/jewelleryThunks";

const initialState = {
  redemptions: [],
  selectedRedemption: null,
  customerRedemptions: [],
  pendingApprovals: [], // NEW: For approval dashboard
  stats: null, // NEW: For statistics
  filters: {
    status: null,
    branchId: null,
    customerId: null,
    planType: null, // NEW
    redemptionType: null, // NEW
  },
  pagination: {
    total: 0,
    page: 1,
    pages: 0,
    limit: 20,
  },
  loading: false,
  actionLoading: false, // NEW: For specific actions
  error: null,
};

const redemptionSlice = createSlice({
  name: "redemptions",
  initialState,
  reducers: {
    setRedemptionFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedRedemption: (state) => {
      state.selectedRedemption = null;
    },
    clearRedemptionError: (state) => {
      state.error = null;
    },
    setRedemptionPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    resetRedemptionFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // ==========================================
      // Create Redemption
      // ==========================================
      .addCase(createRedemptionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRedemptionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.redemptions.unshift(action.payload.data);
        state.selectedRedemption = action.payload.data;
      })
      .addCase(createRedemptionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Get All Redemptions
      // ==========================================
      .addCase(getAllRedemptionsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllRedemptionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.redemptions = action.payload.data;
        state.pagination = action.payload.pagination || {
          total: action.payload.data?.length || 0,
          page: 1,
          pages: 1,
          limit: 20,
        };
      })
      .addCase(getAllRedemptionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Get Redemption By ID
      // ==========================================
      .addCase(getRedemptionByIdThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRedemptionByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRedemption = action.payload.data;
      })
      .addCase(getRedemptionByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Get Customer Redemptions
      // ==========================================
      .addCase(getCustomerRedemptionsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerRedemptionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.customerRedemptions = action.payload.data;
      })
      .addCase(getCustomerRedemptionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Approve Redemption
      // ==========================================
      .addCase(approveRedemptionThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approveRedemptionThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.redemptions.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions[index] = action.payload.data;
        }
        if (state.selectedRedemption?._id === action.payload.data._id) {
          state.selectedRedemption = action.payload.data;
        }
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (r) => r._id !== action.payload.data._id
        );
      })
      .addCase(approveRedemptionThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Reject Redemption (NEW)
      // ==========================================
      .addCase(rejectRedemptionThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectRedemptionThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.redemptions.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions[index] = action.payload.data;
        }
        if (state.selectedRedemption?._id === action.payload.data._id) {
          state.selectedRedemption = action.payload.data;
        }
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (r) => r._id !== action.payload.data._id
        );
      })
      .addCase(rejectRedemptionThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Cancel Redemption
      // ==========================================
      .addCase(cancelRedemptionThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelRedemptionThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.redemptions.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions[index] = action.payload.data;
        }
        if (state.selectedRedemption?._id === action.payload.data._id) {
          state.selectedRedemption = action.payload.data;
        }
      })
      .addCase(cancelRedemptionThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Update Payment (NEW)
      // ==========================================
      .addCase(updatePaymentThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updatePaymentThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.redemptions.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions[index] = action.payload.data;
        }
        if (state.selectedRedemption?._id === action.payload.data._id) {
          state.selectedRedemption = action.payload.data;
        }
      })
      .addCase(updatePaymentThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Complete Redemption (NEW)
      // ==========================================
      .addCase(completeRedemptionThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(completeRedemptionThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.redemptions.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions[index] = action.payload.data;
        }
        if (state.selectedRedemption?._id === action.payload.data._id) {
          state.selectedRedemption = action.payload.data;
        }
      })
      .addCase(completeRedemptionThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Get Pending Approvals (NEW)
      // ==========================================
      .addCase(getPendingApprovalsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPendingApprovalsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingApprovals = action.payload.data;
      })
      .addCase(getPendingApprovalsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==========================================
      // Get Statistics (NEW)
      // ==========================================
      .addCase(getRedemptionStatsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRedemptionStatsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data;
      })
      .addCase(getRedemptionStatsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setRedemptionFilters,
  clearSelectedRedemption,
  clearRedemptionError,
  setRedemptionPage,
  resetRedemptionFilters,
} = redemptionSlice.actions;

export default redemptionSlice.reducer;
